import { useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { api, ApiError } from '../../api/client'
import type { Media, Trip } from '../../api/types'
import { ensureSettings, useUploadMedia } from '../../hooks/queries'
import { prepareImage } from '../../lib/images'
import { PhotoIcon, TrashIcon } from '../../components/Icons'
import { rueckfrage } from '../../components/Rueckfrage'

/**
 * Das Symbolbild einer Reise — in der Übersicht anstelle der Buchstaben.
 *
 * **Nur der Reisende**, wie bei allem, was den Bericht nach außen darstellt.
 * Der Server entscheidet das noch einmal; hier steht nur, was gar nicht erst
 * angeboten wird.
 *
 * Zwei Schritte, und der erste ist ein gewöhnlicher Upload: das Bild geht wie
 * jedes andere Foto in die Reise (verkleinert im Browser, gegen dasselbe
 * Kontingent), und danach merkt sich die Reise seine id. Kein eigener
 * Uploadweg — ein zweiter wäre ein zweites Regelwerk.
 */
export function Symbolbild({ trip }: { trip: Trip }) {
  const upload = useUploadMedia(trip.id)
  const client = useQueryClient()
  const feld = useRef<HTMLInputElement>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  async function waehle(datei: File | undefined) {
    if (!datei) return
    setProblem(null)
    setLaeuft(true)
    try {
      const werte = await ensureSettings(client)
      const fertig = await prepareImage(datei, {
        ...(werte ? { maxEdge: werte.image_max_edge, quality: werte.image_quality } : {}),
        reiseVersatz: trip.tz_offset_min ?? null,
      })
      const medium: Media = await upload.mutateAsync({ blob: fertig.blob })
      await api.post<Trip>(`/api/trips/${trip.id}/cover`, { media_id: medium.id })
      // Die Reise steht in beiden Listen -- Übersicht und Reiseangaben.
      await client.invalidateQueries()
    } catch (fehler) {
      setProblem(
        fehler instanceof ApiError && fehler.offline
          ? 'Dafür braucht es Netz. Versuch es später noch einmal.'
          : fehler instanceof Error
            ? fehler.message
            : 'Das Bild ging nicht durch.',
      )
    } finally {
      setLaeuft(false)
    }
  }

  async function weg() {
    if (!(await rueckfrage('Das Symbolbild dieser Reise wegnehmen?', 'Wegnehmen'))) return
    setProblem(null)
    setLaeuft(true)
    try {
      await api.delete<Trip>(`/api/trips/${trip.id}/cover`)
      await client.invalidateQueries()
    } catch (fehler) {
      setProblem(fehler instanceof Error ? fehler.message : 'Das hat nicht geklappt.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Symbolbild</h2>
      <p className="card__lead">
        Was in der Übersicht vor dem Titel steht. Ohne Bild stehen dort die
        Anfangsbuchstaben.
      </p>

      <div className="symbolbild">
        {trip.cover_url ? (
          <img className="symbolbild__vorschau" src={trip.cover_url} alt="" />
        ) : (
          <span className="symbolbild__leer" aria-hidden="true">
            <PhotoIcon size={22} />
          </span>
        )}

        <div className="symbolbild__knoepfe">
          {/* Hier darf die Dateiauswahl alles anbieten: es ist der Reisende,
              und ein Symbolbild ist genau das, was man gerade fotografiert
              hat oder in den Fotos liegen hat. */}
          <input
            ref={feld}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              void waehle(event.target.files?.[0])
              event.target.value = ''
            }}
          />
          <button
            type="button"
            className="button button--small"
            disabled={laeuft}
            onClick={() => feld.current?.click()}
          >
            {laeuft ? 'Einen Moment …' : trip.cover_url ? 'Anderes Bild' : 'Bild wählen'}
          </button>

          {trip.cover_url && (
            <button
              type="button"
              className="button button--small button--outline"
              disabled={laeuft}
              onClick={() => void weg()}
            >
              <TrashIcon size={14} /> Wegnehmen
            </button>
          )}
        </div>
      </div>

      {/* Keine stille Absage: was nicht durchging, steht als Text da. */}
      {problem && (
        <p className="feld__hinweis" role="alert">
          {problem}
        </p>
      )}
    </div>
  )
}
